import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CheckboxField,
  MCButton,
  SearchTextField,
  Spinner,
  PopoverMenu
} from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';
import { api, lang, remCss, scssVariables } from 'src/helpers';
import { Virtuoso } from 'react-virtuoso';

import model from './model';
import { popupModel } from '../index';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import LANG_CONST from 'src/helpers/lang/const';

class PopupSearch extends BaseComponent {
  componentDidMount() {
    model.baseSubscribe(this.subscription);
  }
  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  render() {
    const { hide } = this.props;

    return (
      <SearchTextField
        placeholder={' '}
        template={'2'}
        style={{ flex: 1, width: 'auto' }}
        autoFocus={true}
        onChange={model.handleChangeSearch}
        value={model.listInput('search')}
        hideSearchFn={() => {
          hide();
        }}
        onKeyDown={this.props.onKeyDown ? (e) => this.props.onKeyDown(e, hide) : null}
      />
    );
  }
}

const VirtuosoList = React.forwardRef(({ children, ...props }, ref) => {
  const refEx = useRef();

  useEffect(() => {
    if (children?.length) {
      setTimeout(() => {
        if (refEx?.current?.offsetHeight && model.state.listHeight !== refEx.current.offsetHeight)
          model.setState({
            listHeight: refEx.current.offsetHeight
          });
      }, 1);
    }
  }, [children, refEx]);

  return (
    <div
      {...props}
      ref={(r) => {
        refEx.current = r;
        ref(r);
      }}
    >
      {children}
    </div>
  );
});
VirtuosoList.displayName = 'VirtuosoList';

class PopupList extends BaseComponent {
  componentDidMount() {
    this.init();
    model.baseSubscribe(this.subscription);
    model.refresh();
  }

  init = () => {
    model.reset(true);
    const { url, idKey, valueKey, ...params } = this.props.apiConfig;
    const api = {
      url,
      validation: {
        skip: 'skip',
        limit: 'limit'
      },
      cancellable: true
    };
    model.apiConfig = {
      refresh: api,
      list: api,
      search: api
    };
    model.idKey = idKey || valueKey || 'id';
    model.setStateSilent({
      selectedList: this.props.selectedList || [],
      params,
      listHeight: 200,
      canProcess: this.props.canProcess
    });
  };

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  submit = (e) => {
    e.preventDefault();
    const { save, hide } = this.props;
    let { selectedList = [] } = model.getState();
    save(selectedList);
    hide();
  };

  select = (item) => {
    let { selectedList = [] } = model.getState();
    if (this.props.isMultiple)
      if (selectedList.find((s) => s.value === item.value))
        model.setState({
          selectedList: selectedList.filter((s) => s.value !== item.value)
        });
      else model.setState({ selectedList: [...selectedList, item] });
    else {
      this.props.save(item);
      this.props.hide();
    }
  };

  renderContent = () => {
    const {
      apiConfig,
      isPopover = false,
      expandOptions: expandOptionsProp = [],
      expandOptionsFn,
      NoResultsElement = null
    } = this.props;
    const { valueKey, labelKey, valueFn, labelFn, disabledFn } = apiConfig;
    const items = model.items();
    const loading = model.refreshing();
    let searchQuery = model.listInput('search');
    let noResults = model.noResults();
    let { selectedList = [] } = model.getState();
    let expandOptions = expandOptionsFn ? expandOptionsFn(searchQuery) : expandOptionsProp;

    if (expandOptions?.length) {
      noResults = false;
    }
    return (
      <div
        className={`mc-flex-col ${isPopover ? 'mc-b' : ''}`}
        style={{
          padding: 0,
          flex: 1,
          backgroundColor: 'white',
          borderRadius: 5
        }}
      >
        <Virtuoso
          style={{ flexGrow: 1, scrollbarGutter: 'auto' }}
          data={[...expandOptions, ...items].map((item) => ({
            value: valueFn ? valueFn(item) : valueKey ? item[valueKey] : item.id,
            label: labelFn ? labelFn(item) : labelKey ? item[labelKey] : item.id,
            item
          }))}
          components={{
            Footer: () =>
              (noResults || loading) && (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  {noResults && !searchQuery && (
                    <>
                      {!NoResultsElement && (
                        <div>
                          {lang.t(this.props.noResultsText || LANG_CONST.text.no_results_yet)}
                        </div>
                      )}
                      {NoResultsElement && <NoResultsElement hide={this.props.hide} />}
                    </>
                  )}
                  {noResults && searchQuery && <div>{lang.t(LANG_CONST.text.nothing_found)}</div>}
                  {loading && <Spinner />}
                </div>
              ),
            List: VirtuosoList
          }}
          endReached={() => {
            if (model.hasMore() && !model.refreshing()) model.loadMore();
          }}
          itemContent={(index, item) => {
            const active = !!selectedList.find((s) => s.value === item.value);
            const disabled = disabledFn ? disabledFn(item.item, selectedList) : false;

            return (
              <div
                className={`mc-flex ${isPopover ? 'mc-p10' : 'mc-p20'} mc-bb 
                 ${active ? 'mc-bg-active' : ''} ${disabled ? 'mc-bg-disabled' : 'mc-pointer mc-bg-hover'}`}
              >
                {!isPopover && (
                  <div className={'mc-flex-col'}>
                    <CheckboxField
                      template={'2'}
                      checked={active}
                      onChange={() => {
                        !disabled && this.select(item);
                      }}
                    />
                  </div>
                )}
                <div
                  className={'mc-grow'}
                  style={{ minWidth: 0 }}
                  onClick={() => !disabled && this.select(item)}
                >
                  {item.label}
                </div>
              </div>
            );
          }}
        />
      </div>
    );
  };

  render() {
    const { closeBtn, isMultiple = false, isPopover = false, styles = {} } = this.props;

    const loading = model.refreshing();
    let noResults = model.noResults();
    let height = model.state.listHeight + (loading || noResults ? 100 : 0);

    if (isPopover) {
      if (!model.listInput('search') && !model.items().length) {
        return null;
      }
      return (
        <div style={{ height: `min(85%, ${height + 2}px)`, ...styles }} className={'mc-flex-5-col'}>
          {this.renderContent()}
        </div>
      );
    }
    return (
      <div
        className={'mc-popup-contain'}
        style={{ width: remCss(500), height: `min(100%, ${model.state.listHeight + 193}px)` }}
      >
        <div className='mc-modal-header'>
          <div style={{ margin: -10 }}>
            <SearchTextField
              template={'2'}
              width={300}
              autoFocus={true}
              onChange={model.handleChangeSearch}
              onKeyDown={this.props.onKeyDown}
              value={model.listInput('search')}
            />
          </div>
          {closeBtn}
        </div>
        {this.renderContent()}
        {isMultiple && !noResults && !loading && (
          <div className='mc-modal-footer mc-flex'>
            <div className={'mc-grow'} />
            <MCButton primary dark radius medium onClick={this.submit}>
              {lang.t(LANG_CONST.buttons.select)}
            </MCButton>
          </div>
        )}
      </div>
    );
  }
}

const getPopupSelectCaches = () => model.state.caches || {};

const setPopupSelectCache = (cacheId, items) => {
  model.setStateSilent({
    caches: {
      ...getPopupSelectCaches(),
      [cacheId]: items
    }
  });
};

const PopupSelect = (props) => {
  const {
    label,
    description,
    template,
    initSelectedParamKey,
    value,
    isMultiple = false,
    onChange,
    name,
    apiConfig,
    placeholder,
    placeholderTxt: placeholderTxtProp,
    defaultValue,
    selectedLabelFn,
    expandOptions: expandOptionsProp = [],
    expandOptionsFn,
    isPopover = false,
    isClearable = true,
    invalid = {},
    styles = {},
    iconSize = 20
  } = props;

  const cacheId = name + '_' + (!isMultiple ? value : (value || [])?.join('|'));
  const [options, setOptions] = useState(getPopupSelectCaches()[cacheId] || []);
  const [loadedDefault, setLoadedDefault] = useState(false);
  let selectedList = options.filter(
    (i) => (isMultiple && value?.includes(i.value)) || i.value === value
  );

  const getExpandOptions = useCallback(
    (query) => {
      if (expandOptionsFn) return expandOptionsFn(query);
      return expandOptionsProp;
    },
    [expandOptionsFn, expandOptionsProp]
  );

  useEffect(() => {
    let ignore = false;
    const { url, valueKey, labelKey, valueFn, labelFn, ...params } = apiConfig;
    let reqParams = null;
    let hasValue = !(value === null || value === undefined || (isMultiple && !value.length));

    if (initSelectedParamKey && !selectedList.length && hasValue) {
      reqParams = {
        skip: 0,
        limit: 10,
        ...params
      };
      if (typeof initSelectedParamKey === 'string') {
        reqParams[initSelectedParamKey] = value;
      } else {
        reqParams = { ...reqParams, ...initSelectedParamKey(value) };
      }
    } else if (defaultValue && !hasValue && !loadedDefault) {
      reqParams = {
        skip: 0,
        limit: 1,
        ...params
      };
    }

    if (reqParams) {
      const request = api(url, reqParams);

      request.then(({ error, data }) => {
        if (error === 'cancel' || ignore) return;

        let expandOptions = getExpandOptions(data.query);
        let items = [...expandOptions, ...(data?.items ? data.items : data || [])].map((item) => ({
          value: valueFn ? valueFn(item) : valueKey ? item[valueKey] : item.id,
          label: labelFn ? labelFn(item) : labelKey ? item[labelKey] : item.id,
          item
        }));
        setOptions(items);
        let cId = cacheId;

        setLoadedDefault(true);
        if (defaultValue && !value?.length) {
          const v = items.length ? items[0] : null;
          onChange(name, v && (isMultiple ? [v.value] : v.value), v);
          cId = name + '_' + v;
        }
        setPopupSelectCache(cId, items);
      });
    }

    return () => {
      ignore = true;
    };
  }, [
    apiConfig,
    cacheId,
    defaultValue,
    initSelectedParamKey,
    isMultiple,
    loadedDefault,
    name,
    onChange,
    selectedList.length,
    value
  ]);

  const placeholderTxt = placeholderTxtProp
    ? placeholderTxtProp
    : (lang.t(placeholder) || lang.t('Search')) + '...';

  const labelHtml =
    isMultiple || !selectedList?.length ? (
      <div className={`mc-label-418 mc-grow  mc-pointer mc-text-truncate`} style={{ minWidth: 0 }}>
        {placeholderTxt}
      </div>
    ) : selectedLabelFn ? (
      selectedLabelFn(selectedList[0].item)
    ) : (
      <div className={`mc-text-418 mc-grow  mc-pointer mc-text-truncate`} style={{ minWidth: 0 }}>
        {selectedList[0].label}
      </div>
    );

  const propList = {
    ...props,
    selectedList,
    save: (data) => {
      setOptions(isMultiple ? data : [data]);
      const v = data && (isMultiple ? data.map((item) => item.value) : data.value);
      onChange(name, v, data);
      const cacheId = name + '_' + (isMultiple ? v : [v])?.join('|');
      setPopupSelectCache(cacheId, isMultiple ? data : [data]);
    }
  };

  const icons = (
    <>
      {!isMultiple && !!selectedList?.length && isClearable && (
        <MCButton
          text
          iconname={BASE_ICONS.CLOSE}
          width={20}
          type={'button'}
          onClick={(e) => {
            e.stopPropagation();
            setOptions([]);
            onChange(name, null);
          }}
          style={{ height: remCss(20) }}
        />
      )}
      {(isMultiple || !selectedList?.length || !isClearable) && (
        <SVGIcon
          iconname={BASE_ICONS.SEARCH}
          width={20}
          fill={scssVariables['colors-mc-text']}
          strokeWidthIcon={0.5}
        />
      )}
    </>
  );
  let content = (
    <div
      className={'select__control mc-flex mc-grow mc-plr15 mc-ptb10'}
      onClick={() => {
        popupModel.setActivePopup('popup_select', {
          active: true,
          component: PopupList,
          props: propList
        });
      }}
    >
      {labelHtml}
      {icons}
    </div>
  );

  if (isPopover)
    content = (
      <div className={'mc-flex-0-col'} style={{ minWidth: 90 }}>
        <PopoverMenu
          triggerBoundsWidth={true}
          autoToggle={false}
          popupMenuStyle={{ height: '100%', boxShadow: 'none', border: 'none' }}
          placement={'bottom-start'}
          possiblePlacements={['bottom-start']}
          menu={(close, isOpen) => isOpen && <PopupList {...propList} hide={close} />}
        >
          {(isOpen, close, triggerProps) =>
            isOpen ? (
              <div {...triggerProps} style={{ display: 'block' }}>
                <PopupSearch hide={close} onKeyDown={props.onKeyDown} />
              </div>
            ) : (
              <div
                {...triggerProps}
                onClick={close}
                className={'select__control mc-flex mc-grow mc-plr15 mc-ptb10'}
              >
                {labelHtml}
                {icons}
              </div>
            )
          }
        </PopoverMenu>
      </div>
    );

  return (
    <div
      className={'mc-select-field-group mc-grow template-' + template}
      style={{ alignItems: 'initial', ...styles }}
    >
      {label && <label>{lang.t(label)}</label>}
      {content}
      {description && <div className={'mc-form-description'}>{lang.t(description)}</div>}
      {invalid[name] && <p className='mc-form-invalid-text'>{invalid[name]}</p>}
      {isMultiple && !!selectedList.length && (
        <div className={'mc-flex mc-wrap mc-flex-start'}>
          {selectedList.map((i) => (
            <div key={i.value} className={'mc-multi-option-value'}>
              {i.label}
              <MCButton
                primary
                iconname={BASE_ICONS.CLOSE}
                width={iconSize}
                type={'button'}
                onClick={() => {
                  const a = selectedList.filter((old) => old.value !== i.value);
                  onChange(name, a && a.map((item) => item.value), a);
                }}
                style={{ height: remCss(iconSize) }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PopupSelect;
